import apiClient from './api.ts';
import { RegisterStudentSchema, StudentInputSchema } from "@/types/student_schema.ts";
import { EnhancedParentRelationshipSchema } from "@/types/parent_schema.ts";

export default {
  async registerStudent(payload: RegisterStudentSchema) {
    return await apiClient.post(`auth/register/student`, payload);
  },
  async importStudents(payload: RegisterStudentSchema[]) {
    return await apiClient.post(`auth/register/students`, payload);
  },
  async validateStudentsImport(file: File, options: Record<string, string | boolean> = {}) {
    const formData = new FormData();
    formData.append('file', file);
    Object.entries(options).forEach(([key,value]) => formData.append(key,String(value)));
    return await apiClient.post(`/students/import/validate`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      validateStatus: (status) => status === 200 || status === 400 // Accept 400 for validation errors
    });
  },
  async importStudentsFromFile(file: File, options: Record<string, string | boolean> = {}) {
    const formData = new FormData();
    formData.append('file', file);
    Object.entries(options).forEach(([key,value]) => formData.append(key,String(value)));
    return await apiClient.post(`/students/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      validateStatus: (status) => status === 200 || status === 400 // Accept 400 for import errors
    });
  },
  async getStudentBySchoolId(isActive: boolean, page: number = 1, limit: number = 20, sort: string = 'id', order: 'asc' | 'desc' = 'asc', search: string = '', searchField: string = '', classId?: number, gradeId?: number) {
    const params = new URLSearchParams({
      is_active: isActive.toString(),
      page: page.toString(),
      limit: limit.toString(),
      sort,
      order
    });
    if (search && search.length >= 2) {
      params.set('search', search);
      if (searchField) params.set('search_field', searchField);
    }
    if (classId) params.set('class_id', classId.toString());
    if (gradeId) params.set('grade_id', gradeId.toString());
    return await apiClient.get(`students?${params}`);
  },
  async getStudentsList() {
    return await apiClient.get('students/list');
  },
  async getStudentsByGradeAllSchools(gradeId: number) {
    return await apiClient.get(`students/all-schools/${gradeId}`);
  },
  async getStudentsByGrade(gradeId: number) {
    return await apiClient.get(`students/grade/${gradeId}`);
  },
  async getStudentById(id: number) {
    return await apiClient.get(`students/${id}`);
  },
  async getStudentByUserId(userId: number) {
    return await apiClient.get(`students/user/${userId}`);
  },
  async getCurrentUserStudent() {
    return await apiClient.get('students/user');
  },
  async getStudentsByParentId(parentId: number) {
    return await apiClient.get(`students/parent/${parentId}`);
  },
  async getStudentEnrollmentHistory(studentId: number) {
    return await apiClient.get(`students/${studentId}/enrollment-history`);
  },
  async updateStudent(studentId: number, student: StudentInputSchema) {
    return await apiClient.put(`students/${studentId}`, student);
  },
  async deleteStudent(studentId: number) {
    return await apiClient.delete(`students/${studentId}`);
  },
  async archiveStudent(studentId: number) {
    return await apiClient.put(`students/${studentId}/archive`);
  },
  async unarchiveStudent(studentId: number) {
    return await apiClient.put(`students/${studentId}/unarchive`);
  },
  async updatePhoneNumberStudent(studentId: number, userId: number, oldPhoneNumber: string, phoneNumber: string) {
    const params = new URLSearchParams({
      oldPhoneNumber,
      phoneNumber
    });

    return await apiClient.put(`students/${studentId}/user/${userId}/phone-number?${params}`);
  },
  async updateGradeStudent(studentId: number, gradeId: number, classId: number, academicYearId?: number) {
    const params = new URLSearchParams();
    if (academicYearId) params.set('academic_year_id', academicYearId.toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    return await apiClient.put(`students/${studentId}/grade/${gradeId}/class/${classId}${query}`);
  },
  async assignPlacementGrade(studentId: number, gradeId: number) {
    return await apiClient.put(`students/${studentId}/placement-grade/${gradeId}`);
  },
  async uploadProfile(studentId: number, data: FormData) {
    return await apiClient.post(`students/${studentId}/profile/upload`, data, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
  },
  async getParentByStudentId(studentId: number) {
    return await apiClient.get(`students/${studentId}/parents`);
  },
  async downloadExcelTemplate(count: number = 10, includeUsername: boolean = false, includePassword: boolean = false, lang: string = 'en') {
    const params = new URLSearchParams({
      count: count.toString(),
      lang: lang
    });

    if (includeUsername) {
      params.append('include_username', 'true');
    }

    if (includePassword) {
      params.append('include_password', 'true');
    }

    return await apiClient.get(`students/template/excel?${params}`, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    });
  },

  // Enhanced student registration with flexible parent linking
  async registerStudentWithParents(payload: {
    student_info: {
      first_name: string;
      last_name: string;
      phone_number: string;
      gender: string;
      date_of_birth: string;
      address: {
        address: string;
        city: string;
        state: string;
        zip_code?: string;
        country: string;
      };
      grade_id: number;
      class_id: number;
    };
    parent_relationships: EnhancedParentRelationshipSchema[];
    user_info?: {
      username: string;
      password: string;
    };
  }) {
    return await apiClient.post(`enhanced-student-parent/register-student-with-parents`, payload);
  },

  // Transfer student to another branch
  async transferStudentToBranch(studentId: number, payload: { branch_id: string; transfer_reason?: string }) {
    return await apiClient.put(`students/${studentId}/transfer`, payload);
  },

  // Get branches associated with a user
  async getUserBranches(userId: number) {
    return await apiClient.get(`users/${userId}/branches`);
  },

  async resetPassword(userId: number, newPassword: string) {
    return await apiClient.post(`users/${userId}/reset-password`, { new_password: newPassword });
  },
};
